import { Section, Card, KPI, Table, PctBar, useMoney, shortDate, periodLabel } from "./shared";

function MiniRank({ title, rows, valueFn, subFn, testid }) {
  return (
    <Card title={title} testid={testid}>
      {rows.length === 0 ? <div className="text-xs text-zinc-400 italic">Sin datos.</div> : (
        <ol className="space-y-1.5 text-xs">
          {rows.map((c, i) => (
            <li key={c.client_id} className="flex items-center gap-2">
              <span className="w-4 text-zinc-400 numeric">{i + 1}</span>
              <span className="flex-1 min-w-0"><span className="block font-medium text-zinc-900 truncate">{c.name}</span>{subFn && <span className="block text-[10px] text-zinc-500">{subFn(c)}</span>}</span>
              <span className="numeric font-semibold text-zinc-800 whitespace-nowrap">{valueFn(c)}</span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

export default function ClientsSection({ r }) {
  const { fmt } = useMoney();
  const c = r.clients;
  const ret = r.retention;
  return (
    <>
      <Section id="clientes" num={3} title="Clientes" desc="Nuevo = primera compra registrada en este mes. Recurrente = compró más de una vez en el mes. Regresó = ya había comprado en meses anteriores.">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <Card title={`Clientes nuevos · ${c.new.length}`} desc="Primera compra en el mes" testid="mr-clients-new">
            <Table rows={c.new} keyFn={(x) => x.client_id} empty="Sin clientes nuevos." cols={[
              { key: "name", label: "Cliente", render: (x) => <div><div className="font-medium">{x.name}</div><div className="text-[10px] text-zinc-500">1ª compra {shortDate(x.first_purchase)}</div></div> },
              { key: "purchases", label: "Compras", align: "right" },
              { key: "total_mxn", label: "Total", align: "right", render: (x) => fmt(x.total_mxn) },
            ]} />
          </Card>
          <Card title={`Recurrentes en el mes · ${c.recurrent.length}`} desc="Más de una compra dentro del mes" testid="mr-clients-recurrent">
            <Table rows={c.recurrent} keyFn={(x) => x.client_id} empty="Nadie compró más de una vez este mes." cols={[
              { key: "name", label: "Cliente", cls: "font-medium" },
              { key: "purchases", label: "Veces", align: "right" },
              { key: "total_mxn", label: "Total", align: "right", render: (x) => fmt(x.total_mxn) },
            ]} />
          </Card>
          <Card title={`Clientes que regresaron · ${c.returned.length}`} desc="Ya habían comprado antes y volvieron" testid="mr-clients-returned">
            <Table rows={c.returned} keyFn={(x) => x.client_id} empty="Ningún cliente anterior compró este mes." cols={[
              { key: "name", label: "Cliente", render: (x) => <div><div className="font-medium">{x.name}</div><div className="text-[10px] text-zinc-500">cliente desde {shortDate(x.first_purchase)}</div></div> },
              { key: "hist", label: "Hist.", align: "right", render: (x) => <span title="Compras históricas">{x.historic_purchases}</span> },
              { key: "month_purchases", label: "Mes", align: "right" },
              { key: "month_total_mxn", label: "Total mes", align: "right", render: (x) => fmt(x.month_total_mxn) },
              { key: "historic_total_mxn", label: "Total hist.", align: "right", render: (x) => <span className="text-zinc-500">{fmt(x.historic_total_mxn)}</span> },
            ]} />
          </Card>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MiniRank title="Mayor número de compras (mes)" rows={c.top_by_purchases} valueFn={(x) => `${x.purchases} compra${x.purchases === 1 ? "" : "s"}`} subFn={(x) => `${x.historic_purchases} históricas`} testid="mr-rank-purchases" />
          <MiniRank title="Mayor gasto acumulado (mes)" rows={c.top_by_spend} valueFn={(x) => fmt(x.total_mxn)} subFn={(x) => `hist. ${fmt(x.historic_total_mxn)}`} testid="mr-rank-spend" />
          <MiniRank title="Mayor ticket promedio (mes)" rows={c.top_by_ticket} valueFn={(x) => fmt(x.avg_ticket_mxn)} subFn={(x) => `${x.purchases} compra${x.purchases === 1 ? "" : "s"}`} testid="mr-rank-ticket" />
        </div>
      </Section>

      <Section id="retencion" num={4} title="Retención de clientes" desc={`Clientes cuya primera compra fue antes de ${periodLabel(r.period)} y cuántos volvieron a comprar (hasta el cierre de este mes).`}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Compraron por 1ª vez antes" value={ret.prior_first_buyers} sub="clientes" testid="kpi-ret-prior" />
          <KPI label="Volvieron a comprar" value={ret.came_back} tone="good" testid="kpi-ret-back" />
          <KPI label="% que regresó" value={`${ret.pct}%`} tone={ret.pct >= 30 ? "good" : ret.pct > 0 ? "warn" : "muted"} testid="kpi-ret-pct" />
          <KPI label="Tiempo entre compras" value={ret.avg_days_between_purchases === null ? "—" : ret.avg_days_between_purchases < 1 ? "< 1 día" : `${Math.round(ret.avg_days_between_purchases)} días`} sub={`promedio · ${ret.gap_samples} intervalos`} testid="kpi-ret-gap" />
        </div>
        <Card title="Por mes de primera compra" desc="De los clientes que se estrenaron en cada mes, cuántos han vuelto." testid="mr-cohorts">
          <Table rows={ret.cohorts} keyFn={(x) => x.month} empty="Aún no hay meses anteriores con clientes." cols={[
            { key: "month", label: "1ª compra en", render: (x) => periodLabel(x.month), cls: "font-medium" },
            { key: "clients", label: "Clientes", align: "right" },
            { key: "returned", label: "Volvieron", align: "right" },
            { key: "pct", label: "% retención", render: (x) => <PctBar pct={x.pct} color={x.pct >= 30 ? "#00873D" : "#FFC000"} /> },
          ]} />
        </Card>
      </Section>
    </>
  );
}
